import { useEffect, useRef } from "react";


const SCRIPT_ID = "commandix-cloudflare-turnstile";
const SCRIPT_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

export const TURNSTILE_ENABLED = process.env.REACT_APP_TURNSTILE_ENABLED === "true";
export const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || "";

let scriptPromise;

function loadTurnstileScript() {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (scriptPromise) return scriptPromise;

  scriptPromise = new Promise((resolve, reject) => {
    const existing = document.getElementById(SCRIPT_ID);
    const script = existing || document.createElement("script");
    const onLoad = () => window.turnstile ? resolve(window.turnstile) : reject(new Error("Turnstile unavailable"));
    const onError = () => reject(new Error("Turnstile script failed"));

    script.addEventListener("load", onLoad, { once: true });
    script.addEventListener("error", onError, { once: true });
    if (!existing) {
      script.id = SCRIPT_ID;
      script.src = SCRIPT_SRC;
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }
  }).catch((error) => {
    const failedScript = document.getElementById(SCRIPT_ID);
    if (failedScript && !window.turnstile) failedScript.remove();
    scriptPromise = undefined;
    throw error;
  });
  return scriptPromise;
}

export default function CloudflareTurnstile({
  action,
  onTokenChange,
  enabled = TURNSTILE_ENABLED,
  siteKey = TURNSTILE_SITE_KEY,
}) {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);

  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    onTokenChange("");

    if (!siteKey) return undefined;

    loadTurnstileScript()
      .then((turnstile) => {
        if (cancelled || !containerRef.current) return;
        widgetIdRef.current = turnstile.render(containerRef.current, {
          sitekey: siteKey,
          action,
          theme: "dark",
          size: "flexible",
          language: "es",
          callback: (token) => onTokenChange(token),
          "expired-callback": () => onTokenChange(""),
          "error-callback": () => onTokenChange(""),
        });
      })
      .catch(() => {
        if (!cancelled) onTokenChange("");
      });

    return () => {
      cancelled = true;
      if (widgetIdRef.current !== null && window.turnstile) {
        try { window.turnstile.remove(widgetIdRef.current); } catch { /* already removed */ }
      }
      widgetIdRef.current = null;
    };
  }, [action, enabled, onTokenChange, siteKey]);

  if (!enabled) return null;
  if (!siteKey) {
    return (
      <p className="text-sm text-center text-destructive" data-testid="turnstile-config-error">
        La verificacion de seguridad no esta disponible. Intenta de nuevo mas tarde.
      </p>
    );
  }

  return <div ref={containerRef} className="min-h-[65px] w-full" data-testid={`turnstile-${action}`} />;
}
